import { maxBy, minBy, partial } from 'lodash';
import { byId } from 'utils/common';

import products from 'domains/products';

import type {
  SavedArrangementCanvas,
  UnavailableArrangementItem,
} from 'domains/saved_arrangements';
import type { Dimension, Point } from 'types/common_types';
import type { CanvasConfiguration } from 'domains/canvases';
import type { RootState } from 'store/wos_store';
import type { ProductTemplateAPIData } from 'types/catalog_types';

type CanvasPosition = Pick<CanvasConfiguration, 'position' | 'id'>;

export const calculateArrangementWidth = partial(calculateArrangementDimension, 'width');
export const calculateArrangementHeight = partial(calculateArrangementDimension, 'height');
function calculateArrangementDimension(
  dimension: 'width' | 'height',
  canvasPositions: CanvasPosition[],
  canvasesById: Record<string, CanvasConfiguration>,
  productsById: Record<number, ProductTemplateAPIData>
) {
  if (
    canvasPositions.length === 0 ||
    Object.keys(canvasesById).length === 0 ||
    Object.keys(productsById).length === 0
  ) {
    return 0;
  }

  const configuredCanvases = canvasPositions
    .map((c) => canvasesById[c.id])
    .filter((c) => !!c.product);

  if (configuredCanvases.length !== canvasPositions.length) return 0;

  const point = dimension === 'width' ? 'x' : 'y';
  const start = minBy(canvasPositions, (c) => c.position[point]);

  const end = maxBy(canvasPositions, (canvasPosition) => {
    const canvas = canvasesById[canvasPosition.id];
    const canvasProduct = productsById[canvas.product as number]; // safe since we already checked for this
    const dimensionSize = products.utils.calculateProductSize(canvasProduct, canvas)[dimension];

    return canvasPosition.position[point] + dimensionSize;
  });

  if (!end || !start) return 0;

  const endCanvas = canvasesById[end.id];
  const endProduct = productsById[endCanvas.product as number]; // safe since we already checked for this
  const endDimensions = products.utils.calculateProductSize(endProduct, endCanvas);

  return Math.abs(end.position[point] + endDimensions[dimension]) - start.position[point];
}

export function findArrangementPositionToCenter(
  arrangementDimensions: Dimension,
  screenDimensions: Dimension
): Point {
  const { width: arrWidth, height: arrHeight } = arrangementDimensions;
  const { width: screenWidth, height: screenHeight } = screenDimensions;

  const x = screenWidth / 2 - arrWidth / 2;
  const y = screenHeight / 2 - arrHeight / 2;

  return { x, y };
}

// Calculates the positions of canvases within the arrangement
// Need to use this to recalculate when a canvas is moved or removed
export function calculateArrangementCanvasPositions(canvases: CanvasPosition[]): {
  updates: { id: string; changes: CanvasPosition }[];
  leftmost?: CanvasPosition;
  topmost?: CanvasPosition;
} {
  const leftmost = minBy(canvases, (c) => c.position.x);
  const topmost = minBy(canvases, (c) => c.position.y);

  if (!leftmost || !topmost) {
    return { updates: [] };
  }

  // Update the other canvas positions within the repositioned arrangement
  const updates = canvases.map((canvasToUpdate) => {
    const changes = {
      id: canvasToUpdate.id,
      position: {
        x: canvasToUpdate.position.x - leftmost.position.x,
        y: canvasToUpdate.position.y - topmost.position.y,
      },
    };
    return { id: canvasToUpdate.id, changes };
  });

  return { updates, leftmost, topmost };
}

// Determines which canvases in a saved arrangement are currently unavailable or discontinued
export function getArrangementAvailability(state: RootState, canvases: SavedArrangementCanvas[]) {
  const unavailability: UnavailableArrangementItem[] = [];
  const discontinued = 'discontinued';
  const unavailable = 'unavailable';

  canvases.forEach((canvas) => {
    const { id, product: productId, options: optionConfig } = canvas.config;
    const product = state.products.entities[productId];

    // Very unlikely that a product would be temporarily unavailable, even less likely
    // that a temporarily unavailable product is able to be in an arrangement
    if (product && product.unavailable) {
      unavailability.push({
        type: 'product',
        reason: unavailable,
        item: { id, name: product.name_long, parentName: 'Size' },
      });
    } else if (!product) {
      unavailability.push({ type: 'product', reason: discontinued, item: { id } });
    }

    // No sense to check the options if product can't be loaded
    if (!product || product.unavailable) return;

    Object.entries(optionConfig).forEach(([optionGroupId, optionId]) => {
      const optionGroup = state.optionGroups.entities[optionGroupId];
      const option = optionGroup?.options.find(byId(optionId));

      // Will never be the case that an option group is temporarily unavailable
      if (!optionGroup) {
        unavailability.push({ type: 'option-group', reason: discontinued, item: { id } });
        return;
      }

      if (option && option.unavailable) {
        unavailability.push({
          type: 'option',
          reason: unavailable,
          item: { id, name: option.name, parentName: optionGroup.name },
        });
      } else if (!option) {
        unavailability.push({ type: 'option', reason: discontinued, item: { id } });
      }
    });
  });

  return unavailability;
}
